import {
    Component,
    OnInit,
    OnDestroy,
    inject,
    signal,
    input,
    output,
    effect,
    OnChanges,
    SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormGroup, FormBuilder, Validators } from '@angular/forms';
import { SampleRetentionAnnex } from '@qc/shared';
import { InputTextModule } from 'primeng/inputtext';
import { TextareaModule } from 'primeng/textarea';
import { Button } from 'primeng/button';
import { CheckboxModule } from 'primeng/checkbox';
import { SelectModule } from 'primeng/select';
import { RadioButtonModule } from 'primeng/radiobutton';
import { Subject, takeUntil } from 'rxjs';
import { ClientRecordsService } from '../../../core/services/client-records.service';
import { OrganizationType } from '@qc/shared/enums';
import { CreateOrganizationRequestDto, OrganizationResponseDto, UpdateOrganizationRequestDto } from '@qc/shared/dto';
import { OrganizationContactsManagerComponent } from './organization-contacts-manager.component';
import { ContactWithRole } from '../../interfaces/contact.interface';
import { EntityCacheService } from '../../../core/services/entity-cache.service';

@Component({
    selector: 'qc-organization-form',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        InputTextModule,
        TextareaModule,
        Button,
        CheckboxModule,
        SelectModule,
        RadioButtonModule,
        OrganizationContactsManagerComponent,
    ],
    template: `
        <div class="space-y-4">
            <div *ngIf="mode() === 'edit'" class="mb-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div class="flex items-start">
                    <span class="material-symbols-outlined w-5 h-5 text-yellow-600 mt-0.5 mr-3"> warning </span>
                    <div>
                        <h4 class="text-sm font-medium text-yellow-800">Global Resource Update</h4>
                        <p class="text-sm text-yellow-700 mt-1">
                            Changes to this organization will be visible in all Client Information Records where this
                            organization is assigned.
                        </p>
                    </div>
                </div>
            </div>

            <form [formGroup]="form" class="space-y-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                        <label class="block text-sm mb-1">Organization Name *</label>
                        <input
                            pInputText
                            formControlName="organizationName"
                            placeholder="Enter organization name"
                            class="w-full"
                            [class.border-red-500]="hasFieldError('organizationName')"
                        />
                        <div *ngIf="hasFieldError('organizationName')" class="text-rose-500 text-sm mt-1">
                            {{ getFieldError('organizationName') }}
                        </div>
                    </div>

                    <div>
                        <label class="block text-sm mb-1">Organization Type *</label>
                        <p-select
                            formControlName="organizationType"
                            [options]="organizationTypes"
                            placeholder="Select type"
                            optionLabel="label"
                            optionValue="value"
                            styleClass="w-full"
                            [class.border-red-500]="hasFieldError('organizationType')"
                        >
                        </p-select>
                        <div *ngIf="hasFieldError('organizationType')" class="text-rose-500 text-sm mt-1">
                            {{ getFieldError('organizationType') }}
                        </div>
                    </div>

                    <div class="md:col-span-2">
                        <label class="block text-sm mb-1">Address</label>
                        <textarea
                            pTextarea
                            formControlName="address"
                            placeholder="Complete address"
                            rows="2"
                            class="w-full"
                            [autoResize]="true"
                        >
                        </textarea>
                    </div>

                    <div class="md:col-span-2">
                        <label class="block text-sm mb-1">Facility Licence</label>

                        <div class="border border-gray-300 rounded-lg overflow-hidden mb-4">
                            <table class="w-full">
                                <thead class="bg-gray-50">
                                    <tr>
                                        <th class="px-4 py-3 text-left ">Licence Type</th>
                                        <th class="px-4 py-3 text-center ">DEL</th>
                                        <th class="px-4 py-3 text-center ">NHP Site Licence</th>
                                        <th class="px-4 py-3 text-center ">MDEL</th>
                                        <th class="px-4 py-3 text-center ">Dealer's Licence</th>
                                        <th class="px-4 py-3 text-center ">N/A</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr class="border-t border-gray-200">
                                        <td class="px-4 py-3 ">Select applicable licences</td>
                                        <td class="px-4 py-3 text-center">
                                            <p-checkbox formControlName="hasDel" [binary]="true"></p-checkbox>
                                        </td>
                                        <td class="px-4 py-3 text-center">
                                            <p-checkbox
                                                formControlName="hasNhpSiteLicence"
                                                [binary]="true"
                                            ></p-checkbox>
                                        </td>
                                        <td class="px-4 py-3 text-center">
                                            <p-checkbox formControlName="hasMdel" [binary]="true"></p-checkbox>
                                        </td>
                                        <td class="px-4 py-3 text-center">
                                            <p-checkbox
                                                formControlName="hasDealersLicence"
                                                [binary]="true"
                                            ></p-checkbox>
                                        </td>
                                        <td class="px-4 py-3 text-center">
                                            <p-checkbox formControlName="hasNA" [binary]="true"></p-checkbox>
                                        </td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>

                        <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div *ngIf="form.get('hasDel')?.value">
                                <label class="block text-sm mb-1">DEL Number</label>
                                <input
                                    pInputText
                                    formControlName="delNumber"
                                    placeholder="Enter DEL number"
                                    class="w-full"
                                />
                            </div>
                            <div *ngIf="form.get('hasNhpSiteLicence')?.value">
                                <label class="block text-sm mb-1">NHP Site Licence Number</label>
                                <input
                                    pInputText
                                    formControlName="nhpSiteLicence"
                                    placeholder="Enter NHP site licence number"
                                    class="w-full"
                                />
                            </div>
                            <div *ngIf="form.get('hasMdel')?.value">
                                <label class="block text-sm mb-1">MDEL Number</label>
                                <input
                                    pInputText
                                    formControlName="mdelNumber"
                                    placeholder="Enter MDEL number"
                                    class="w-full"
                                />
                            </div>
                            <div *ngIf="form.get('hasDealersLicence')?.value">
                                <label class="block text-sm mb-1">Dealer's Licence Number</label>
                                <input
                                    pInputText
                                    formControlName="dealersLicence"
                                    placeholder="Enter dealer's licence number"
                                    class="w-full"
                                />
                            </div>
                        </div>
                    </div>

                    <div class="md:col-span-2">
                        <label class="block text-sm mb-1">Notes</label>
                        <textarea
                            pTextarea
                            formControlName="notes"
                            placeholder="Notes about this organization"
                            rows="3"
                            class="w-full"
                            [autoResize]="true"
                        ></textarea>
                    </div>

                    <div
                        *ngIf="
                            form.get('organizationType')?.value === OrganizationType.WHOLESALER ||
                            form.get('organizationType')?.value === OrganizationType.DISTRIBUTOR
                        "
                        class="md:col-span-2"
                    >
                        <label class="block text-sm mb-1">Is the Wholesale Licence Confirmed?</label>
                        <div class="flex gap-6 mb-3">
                            <div class="flex items-center">
                                <p-radioButton
                                    formControlName="wholesaleLicenseMethod"
                                    value="licence_on_hand"
                                    inputId="wholesaleLicenseMethodOnHand"
                                />
                                <label for="wholesaleLicenseMethodOnHand" class="ml-2"
                                    >Confirmed, copy of licence on hand</label
                                >
                            </div>
                            <div class="flex items-center">
                                <p-radioButton
                                    formControlName="wholesaleLicenseMethod"
                                    value="health_canada_database"
                                    inputId="wholesaleLicenseMethodHealthCanada"
                                />
                                <label for="wholesaleLicenseMethodHealthCanada" class="ml-2"
                                    >Confirmed on Health Canada Inspection Database</label
                                >
                            </div>
                            <div class="flex items-center">
                                <p-radioButton
                                    formControlName="wholesaleLicenseMethod"
                                    value="unconfirmed"
                                    inputId="wholesaleLicenseMethodUnconfirmed"
                                />
                                <label for="wholesaleLicenseMethodUnconfirmed" class="ml-2">Unconfirmed</label>
                            </div>
                        </div>
                    </div>

                    <div class="md:col-span-2">
                        <label class="block text-sm mb-1">Quality Agreement</label>
                        <input pInputText formControlName="qualityAgreement" placeholder="e.g., 06 Dec 2021" class="w-full" />
                    </div>

                    <div class="md:col-span-2">
                        <label class="block text-sm mb-3">Alternate Sample Retention Annex in DEL?</label>
                        <div class="flex gap-6">
                            <div class="flex items-center">
                                <p-radioButton
                                    formControlName="sampleRetentionAnnex"
                                    value="Yes"
                                    inputId="sampleRetentionYes"
                                />
                                <label for="sampleRetentionYes" class="ml-2">Yes</label>
                            </div>
                            <div class="flex items-center">
                                <p-radioButton
                                    formControlName="sampleRetentionAnnex"
                                    value="No"
                                    inputId="sampleRetentionNo"
                                />
                                <label for="sampleRetentionNo" class="ml-2">No</label>
                            </div>
                            <div class="flex items-center">
                                <p-radioButton
                                    formControlName="sampleRetentionAnnex"
                                    value="N/A"
                                    inputId="sampleRetentionNA"
                                />
                                <label for="sampleRetentionNA" class="ml-2">N/A</label>
                            </div>
                        </div>
                    </div>

                    <div *ngIf="form.get('sampleRetentionAnnex')?.value === 'Yes'" class="md:col-span-2">
                        <label class="block text-sm mb-1">Sample Retention Comments</label>
                        <textarea
                            pTextarea
                            formControlName="sampleRetentionComments"
                            placeholder="Comments related to sample retention annex"
                            rows="2"
                            class="w-full"
                            [autoResize]="true"
                        ></textarea>
                    </div>

                    <div *ngIf="form.get('organizationType')?.value === 'TESTER'" class="md:col-span-2">
                        <div>
                            <label class="block text-sm mb-2">Testing Service</label>
                            <div class="flex gap-6">
                                <div class="flex items-center">
                                    <p-checkbox
                                        formControlName="testingServiceApi"
                                        binary="true"
                                        inputId="testingServiceApi"
                                    />
                                    <label for="testingServiceApi" class="ml-2">API</label>
                                </div>
                                <div class="flex items-center">
                                    <p-checkbox
                                        formControlName="testingServiceId"
                                        binary="true"
                                        inputId="testingServiceId"
                                    />
                                    <label for="testingServiceId" class="ml-2">ID</label>
                                </div>
                                <div class="flex items-center">
                                    <p-checkbox
                                        formControlName="testingServiceConfirmatory"
                                        binary="true"
                                        inputId="testingServiceConfirmatory"
                                    />
                                    <label for="testingServiceConfirmatory" class="ml-2">CONFIRMATORY</label>
                                </div>
                                <div class="flex items-center">
                                    <p-checkbox
                                        formControlName="testingServiceStability"
                                        binary="true"
                                        inputId="testingServiceStability"
                                    />
                                    <label for="testingServiceStability" class="ml-2">STABILITY</label>
                                </div>
                            </div>
                        </div>

                        <div class="mt-5">
                            <label class="block text-sm mb-1">Special Laboratory Notes</label>
                            <textarea
                                pTextarea
                                formControlName="specialLabNotes"
                                placeholder="Special laboratory notes and requirements"
                                rows="3"
                                class="w-full"
                                [autoResize]="true"
                            ></textarea>
                        </div>

                        <div class="mt-5">
                            <label class="block text-sm mb-1"
                                >Has Client requested Lot not be released until Confirmatory Testing complete?</label
                            >
                            <div class="flex gap-6 mb-3">
                                <div class="flex items-center">
                                    <p-radioButton
                                        formControlName="lotReleaseBlockedUntilConfirmatory"
                                        [value]="true"
                                        inputId="lotReleaseYes"
                                    />
                                    <label for="lotReleaseYes" class="ml-2">Yes</label>
                                </div>
                                <div class="flex items-center">
                                    <p-radioButton
                                        formControlName="lotReleaseBlockedUntilConfirmatory"
                                        [value]="false"
                                        inputId="lotReleaseNo"
                                    />
                                    <label for="lotReleaseNo" class="ml-2">No</label>
                                </div>
                            </div>
                        </div>

                        <div class="mt-5">
                            <label class="block text-sm mb-1">Comments</label>
                            <textarea
                                pTextarea
                                formControlName="lotReleaseComments"
                                placeholder="Comments related to lot release requirements"
                                rows="2"
                                class="w-full"
                                [autoResize]="true"
                            ></textarea>
                        </div>
                    </div>
                </div>
            </form>

            <div *ngIf="showContactsManager()" class="mt-6">
                <qc-organization-contacts-manager
                    [organizationId]="organizationId() || ''"
                    [contacts]="organizationContacts()"
                    [isEditMode]="mode() === 'edit'"
                    (contactsUpdated)="onContactsUpdated()"
                    (contactAdded)="onContactAdded($event)"
                    (contactRemoved)="onContactRemoved($event)"
                />
            </div>

            <div *ngIf="showButtons()" class="flex justify-end gap-2 mt-6">
                <p-button
                    label="Cancel"
                    icon="pi pi-times"
                    severity="secondary"
                    [outlined]="true"
                    (click)="onCancel()"
                />
                <p-button
                    label="Save"
                    icon="pi pi-check"
                    [disabled]="!form.valid || isSubmitting()"
                    [loading]="isSubmitting()"
                    (click)="onSubmit()"
                />
            </div>
        </div>
    `,
})
export class OrganizationFormComponent implements OnInit, OnDestroy, OnChanges {
    private fb = inject(FormBuilder);
    private clientRecordsService = inject(ClientRecordsService);
    private entityCacheService = inject(EntityCacheService);

    OrganizationType = OrganizationType;

    private destroy$ = new Subject<void>();

    mode = input<'create' | 'edit'>('create');
    organization = input<OrganizationResponseDto | null>(null);
    organizationId = input<string | null>(null);
    showContactsManager = input<boolean>(true);
    showButtons = input<boolean>(true);
    deferSave = input<boolean>(false);
    save = output<OrganizationResponseDto>();
    cancel = output<void>();
    organizationDataWithContacts = output<{ organization: OrganizationResponseDto; contacts: ContactWithRole[] }>();

    form!: FormGroup;
    isSubmitting = signal(false);

    organizationContacts = signal<ContactWithRole[]>([]);
    private readonly initialContactIds = new Set<string>();
    private readonly initialContactsMap = new Map<string, ContactWithRole>();
    private readonly deletedContactIds = new Set<string>();
    organizationTypes = [
        { label: 'Client', value: OrganizationType.CLIENT },
        { label: 'Fabricator/Manufacturer', value: OrganizationType.FABRICATOR_MANUFACTURER },
        { label: 'MAH', value: OrganizationType.MAH },
        { label: 'Importer', value: OrganizationType.IMPORTER },
        { label: 'Distributor', value: OrganizationType.DISTRIBUTOR },
        { label: 'Wholesaler', value: OrganizationType.WHOLESALER },
        { label: '3PL', value: OrganizationType.THREE_PL },
        { label: 'API Supplier', value: OrganizationType.API_SUPPLIER },
        { label: 'Primary Labeler', value: OrganizationType.PRIMARY_LABELER },
        { label: 'Primary Packager', value: OrganizationType.PRIMARY_PACKAGER },
        { label: 'Secondary Labeler', value: OrganizationType.SECONDARY_LABELER },
        { label: 'Secondary Packager', value: OrganizationType.SECONDARY_PACKAGER },
        { label: 'Tester - Release', value: OrganizationType.TESTER_RELEASE },
        { label: 'Tester - Release Micro', value: OrganizationType.TESTER_RELEASE_MICRO },
        { label: 'Tester - Full test', value: OrganizationType.TESTER_FULL_TEST },
        { label: 'Tester - Confirmatory', value: OrganizationType.TESTER_CONFIRMATORY },
        { label: 'Tester - ID', value: OrganizationType.TESTER_ID },
        { label: 'Tester - Stability', value: OrganizationType.TESTER_STABILITY },
    ];

    ngOnInit() {
        this.initializeForm();
        this.loadData();

        if (this.organization() && this.mode() === 'edit' && this.form) {
            this.form.patchValue({
                ...this.organization(),
                hasNA: !this.organization()!.isLicenseAvailable,
                qualityAgreement: (this.organization() as any).qualityAgreement || '',
                wholesaleLicenseMethod: (this.organization() as any).wholesaleLicenseMethod || '',
                sampleRetentionAnnex: this.organization()!.sampleRetentionAnnex,
                testingServiceApi: this.organization()!.testingServiceApi || false,
                testingServiceId: this.organization()!.testingServiceId || false,
                testingServiceConfirmatory: this.organization()!.testingServiceConfirmatory || false,
                testingServiceStability: this.organization()!.testingServiceStability || false,
                specialLabNotes: this.organization()!.specialLabNotes || '',
                lotReleaseBlockedUntilConfirmatory: this.organization()!.lotReleaseBlockedUntilConfirmatory || false,
                lotReleaseComments: this.organization()!.lotReleaseComments || '',
            });
        }

        this.form.get('sampleRetentionAnnex')?.valueChanges.subscribe((value) => {
            if (value !== 'Yes') {
                this.form.get('sampleRetentionComments')?.setValue('', { emitEvent: false });
            }
        });

        this.form.get('hasDel')?.valueChanges.subscribe((value) => {
            if (!value) {
                this.form.get('delNumber')?.setValue('', { emitEvent: false });
            }
        });

        this.form.get('hasNhpSiteLicence')?.valueChanges.subscribe((value) => {
            if (!value) {
                this.form.get('nhpSiteLicence')?.setValue('', { emitEvent: false });
            }
        });

        this.form.get('hasMdel')?.valueChanges.subscribe((value) => {
            if (!value) {
                this.form.get('mdelNumber')?.setValue('', { emitEvent: false });
            }
        });

        this.form.get('hasDealersLicence')?.valueChanges.subscribe((value) => {
            if (!value) {
                this.form.get('dealersLicence')?.setValue('', { emitEvent: false });
            }
        });

        this.form.get('hasNA')?.valueChanges.subscribe((value) => {
            if (value) {
                this.form.get('hasDel')?.setValue(false, { emitEvent: false });
                this.form.get('hasNhpSiteLicence')?.setValue(false, { emitEvent: false });
                this.form.get('hasMdel')?.setValue(false, { emitEvent: false });
                this.form.get('hasDealersLicence')?.setValue(false, { emitEvent: false });

                this.form.get('delNumber')?.setValue('', { emitEvent: false });
                this.form.get('nhpSiteLicence')?.setValue('', { emitEvent: false });
                this.form.get('mdelNumber')?.setValue('', { emitEvent: false });
                this.form.get('dealersLicence')?.setValue('', { emitEvent: false });

                this.form.get('hasDel')?.disable({ emitEvent: false });
                this.form.get('hasNhpSiteLicence')?.disable({ emitEvent: false });
                this.form.get('hasMdel')?.disable({ emitEvent: false });
                this.form.get('hasDealersLicence')?.disable({ emitEvent: false });
            } else {
                this.form.get('hasDel')?.enable({ emitEvent: false });
                this.form.get('hasNhpSiteLicence')?.enable({ emitEvent: false });
                this.form.get('hasMdel')?.enable({ emitEvent: false });
                this.form.get('hasDealersLicence')?.enable({ emitEvent: false });
            }
        });

        this.form.get('hasDel')?.valueChanges.subscribe((value) => {
            if (value) {
                this.form.get('hasNA')?.setValue(false, { emitEvent: false });
            }
        });

        this.form.get('hasNhpSiteLicence')?.valueChanges.subscribe((value) => {
            if (value) {
                this.form.get('hasNA')?.setValue(false, { emitEvent: false });
            }
        });

        this.form.get('hasMdel')?.valueChanges.subscribe((value) => {
            if (value) {
                this.form.get('hasNA')?.setValue(false, { emitEvent: false });
            }
        });

        this.form.get('hasDealersLicence')?.valueChanges.subscribe((value) => {
            if (value) {
                this.form.get('hasNA')?.setValue(false, { emitEvent: false });
            }
        });
    }

    private initializeForm() {
        this.form = this.fb.group({
            organizationName: ['', [Validators.required]],
            organizationType: ['', [Validators.required]],
            address: [''],
            hasDel: [false],
            delNumber: [''],
            hasNhpSiteLicence: [false],
            nhpSiteLicence: [''],
            hasMdel: [false],
            mdelNumber: [''],
            hasDealersLicence: [false],
            dealersLicence: [''],
            hasNA: [false],
            notes: [''],
            qualityAgreement: [''],
            wholesaleLicenseMethod: [''],
            sampleRetentionAnnex: [''],
            sampleRetentionComments: [''],
            testingServiceApi: [false],
            testingServiceId: [false],
            testingServiceConfirmatory: [false],
            testingServiceStability: [false],
            specialLabNotes: [''],
            lotReleaseBlockedUntilConfirmatory: [null],
            lotReleaseComments: [''],
        });

        if (this.organization()) {
            const orgData = this.organization()!;
            this.form.patchValue({
                ...orgData,
                hasNA: !orgData.isLicenseAvailable,
                qualityAgreement: (orgData as any).qualityAgreement || '',
                wholesaleLicenseMethod: (orgData as any).wholesaleLicenseMethod || '',
                sampleRetentionAnnex: orgData.sampleRetentionAnnex,
                testingServiceApi: orgData.testingServiceApi || false,
                testingServiceId: orgData.testingServiceId || false,
                testingServiceConfirmatory: orgData.testingServiceConfirmatory || false,
                testingServiceStability: orgData.testingServiceStability || false,
                specialLabNotes: orgData.specialLabNotes || '',
                lotReleaseBlockedUntilConfirmatory: orgData.lotReleaseBlockedUntilConfirmatory || false,
                lotReleaseComments: orgData.lotReleaseComments || '',
            });
        }
    }

    private loadData() {
        if (this.mode() === 'edit' && this.organizationId()) {
            const org = this.organization();
            const orgContacts =
                (org as any)?.organizationContacts || (org as any)?.contacts || (this.organizationContacts() ?? []);

            if (Array.isArray(orgContacts) && orgContacts.length > 0) {
                this.organizationContacts.set(orgContacts as ContactWithRole[]);
                this.initializeContactTracking(orgContacts as ContactWithRole[]);
                return;
            }

            this.loadOrganizationContacts(this.organizationId());
        }
    }

    private initializeContactTracking(contacts: ContactWithRole[]): void {
        const contactList = (contacts as ContactWithRole[]) || [];
        this.initialContactIds.clear();
        this.initialContactsMap.clear();
        this.deletedContactIds.clear();

        contactList.forEach((contact) => {
            if (!contact?.id) {
                return;
            }
            this.initialContactIds.add(contact.id);
            this.initialContactsMap.set(contact.id, { ...contact });
        });
    }

    private emitDeferredContactsChange(): void {
        if (!this.deferSave() || this.mode() !== 'edit') {
            return;
        }

        const organizationId = this.organizationId();
        if (!organizationId) {
            return;
        }

        const baseOrganization = this.organization() || ({ id: organizationId } as any);
        const { contacts: contactsWithFlags, deletedContactIds } = this.buildContactsWithOperationFlags();

        const organizationWithContacts = {
            ...baseOrganization,
            id: organizationId,
            organizationContacts: contactsWithFlags,
            deletedContactIds: deletedContactIds,
        };

        this.save.emit(organizationWithContacts as any);
    }

    private loadOrganizationContacts(organizationId: string | null) {
        if (!organizationId) return;

        this.entityCacheService
            .getOrganizationContacts(organizationId)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
                next: (contacts) => {
                    const contactList = (contacts as ContactWithRole[]) || [];
                    this.organizationContacts.set(contactList);
                    this.initialContactIds.clear();
                    this.initialContactsMap.clear();
                    this.deletedContactIds.clear();
                    contactList.forEach((contact) => {
                        this.initialContactIds.add(contact.id);
                        this.initialContactsMap.set(contact.id, { ...contact });
                    });
                },
                error: (error) => {
                    this.organizationContacts.set([]);
                    this.initialContactIds.clear();
                    this.initialContactsMap.clear();
                    this.deletedContactIds.clear();
                },
            });

    }

    onContactsUpdated() { }

    onContactAdded(contact: ContactWithRole) {
        if (contact?.id) {
            this.deletedContactIds.delete(contact.id);
        }

        this.organizationContacts.update((contacts) => {
            const existingIndex = contacts.findIndex((c) => c.id === contact.id);
            if (existingIndex >= 0) {
                const updated = [...contacts];
                updated[existingIndex] = contact;
                return updated;
            }

            return [...contacts, contact];
        });

        const organizationId = this.organizationId();
        if (organizationId) {
            this.entityCacheService.invalidateOrganizationContacts(organizationId);
        }

        this.emitDeferredContactsChange();
    }

    onContactRemoved(contactId: string) {
        if (this.initialContactIds.has(contactId)) {
            this.deletedContactIds.add(contactId);
        }

        this.organizationContacts.update((contacts) => contacts.filter((c) => c.id !== contactId));

        const organizationId = this.organizationId();
        if (organizationId) {
            this.entityCacheService.invalidateOrganizationContacts(organizationId);
        }
        console.log('onContactRemoved', this.deletedContactIds);
        this.emitDeferredContactsChange();
    }


    private buildContactsWithOperationFlags(): { contacts: any[], deletedContactIds: string[] } {
        const currentContacts = this.organizationContacts();
        const contactsWithFlags = currentContacts.map(contact => {
            const isNew = !this.initialContactIds.has(contact.id);
            const initialContact = this.initialContactsMap.get(contact.id);

            const isUpdated = initialContact && (
                initialContact.roleDescription !== contact.roleDescription ||
                initialContact.context !== contact.context
            );

            return {
                ...contact,
                ...(isNew ? { _isNew: true } : {}),
                ...(isUpdated ? { _isUpdated: true } : {}),
            };
        });

        return {
            contacts: contactsWithFlags,
            deletedContactIds: Array.from(this.deletedContactIds)
        };
    }

    hasFieldError(fieldName: string): boolean {
        const field = this.form.get(fieldName);
        return !!(field && field.invalid && (field.dirty || field.touched));
    }

    getFieldError(fieldName: string): string | null {
        const field = this.form.get(fieldName);
        if (field && field.errors) {
            if (field.errors['required']) {
                return 'This field is required';
            }
        }
        return null;
    }

    onSubmit() {
        if (this.form.valid) {
            this.isSubmitting.set(true);
            const formValue = this.form.value;

            let sampleRetentionAnnex: SampleRetentionAnnex | undefined;
            if (formValue.sampleRetentionAnnex === 'Yes') {
                sampleRetentionAnnex = SampleRetentionAnnex.YES;
            } else if (formValue.sampleRetentionAnnex === 'No') {
                sampleRetentionAnnex = SampleRetentionAnnex.NO;
            } else if (formValue.sampleRetentionAnnex === 'N/A') {
                sampleRetentionAnnex = SampleRetentionAnnex.NA;
            }

            const contacts = this.organizationContacts();

            const organizationData: CreateOrganizationRequestDto = {
                organizationName: formValue.organizationName,
                organizationType: formValue.organizationType,
                address: formValue.address,
                hasDel: formValue.hasDel,
                delNumber: formValue.delNumber,
                hasNhpSiteLicence: formValue.hasNhpSiteLicence,
                nhpSiteLicence: formValue.nhpSiteLicence,
                hasMdel: formValue.hasMdel,
                mdelNumber: formValue.mdelNumber,
                hasDealersLicence: formValue.hasDealersLicence,
                dealersLicence: formValue.dealersLicence,
                isLicenseAvailable: !formValue.hasNA,
                notes: formValue.notes,
                qualityAgreement: formValue.qualityAgreement,
                isWholesaleLicenseConfirmed:
                    formValue.wholesaleLicenseMethod && formValue.wholesaleLicenseMethod !== 'unconfirmed'
                        ? true
                        : undefined,
                wholesaleLicenseMethod: formValue.wholesaleLicenseMethod ? formValue.wholesaleLicenseMethod : undefined,
                sampleRetentionAnnex: sampleRetentionAnnex,
                sampleRetentionComments: formValue.sampleRetentionComments,
                testingServiceApi: formValue.testingServiceApi,
                testingServiceId: formValue.testingServiceId,
                testingServiceConfirmatory: formValue.testingServiceConfirmatory,
                testingServiceStability: formValue.testingServiceStability,
                specialLabNotes: formValue.specialLabNotes,
                lotReleaseBlockedUntilConfirmatory: formValue.lotReleaseBlockedUntilConfirmatory,
                lotReleaseComments: formValue.lotReleaseComments,
                contacts: (contacts || []).map((c) => ({
                    contactId: c.id,
                    roleDescription: c.roleDescription,
                    context: c.context,
                })),
            };

            const organizationId = this.organizationId();

            if (this.deferSave()) {
                const { contacts: contactsWithFlags, deletedContactIds } = this.buildContactsWithOperationFlags();


                if (this.mode() === 'edit' && organizationId) {
                    const organizationWithContacts = {
                        ...organizationData,
                        id: organizationId,
                        organizationContacts: contactsWithFlags,
                        deletedContactIds: deletedContactIds,
                    };
                    this.save.emit(organizationWithContacts as any);
                } else {
                    this.organizationDataWithContacts.emit({
                        organization: organizationData as OrganizationResponseDto,
                        contacts: contactsWithFlags,
                    });
                }

                this.isSubmitting.set(false);
                return;
            }

            if (organizationId && this.mode() === 'edit') {
                const updateData: UpdateOrganizationRequestDto = organizationData;
                this.clientRecordsService.updateOrganization(organizationId, updateData).subscribe({
                    next: async (updatedOrganization) => {
                        const organizationWithContacts = {
                            ...updatedOrganization,
                            organizationContacts: contacts || [],
                        };

                        this.save.emit(organizationWithContacts);
                        this.isSubmitting.set(false);
                    },
                    error: (error) => {
                        this.isSubmitting.set(false);
                    },
                });
            } else {
                this.clientRecordsService.createOrganization(organizationData).subscribe({
                    next: async (newOrganization) => {
                        this.organizationDataWithContacts.emit({
                            organization: newOrganization,
                            contacts: contacts,
                        });

                        this.isSubmitting.set(false);
                    },
                    error: (error) => {
                        this.isSubmitting.set(false);
                    },
                });
            }
        }
    }

    onCancel() {
        this.cancel.emit();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['organization'] && this.organization() && this.mode() === 'edit' && this.form) {
            this.form.patchValue({
                ...this.organization(),
                hasNA: !this.organization()!.isLicenseAvailable,
                sampleRetentionAnnex: this.organization()!.sampleRetentionAnnex,
                testingServiceApi: this.organization()!.testingServiceApi || false,
                testingServiceId: this.organization()!.testingServiceId || false,
                testingServiceConfirmatory: this.organization()!.testingServiceConfirmatory || false,
                testingServiceStability: this.organization()!.testingServiceStability || false,
                specialLabNotes: this.organization()!.specialLabNotes || '',
                lotReleaseBlockedUntilConfirmatory: this.organization()!.lotReleaseBlockedUntilConfirmatory || false,
                lotReleaseComments: this.organization()!.lotReleaseComments || '',
            });

            const orgContacts =
                (this.organization() as any)?.organizationContacts || (this.organization() as any)?.contacts || null;
            if (Array.isArray(orgContacts)) {
                this.organizationContacts.set(orgContacts as ContactWithRole[]);
                this.initializeContactTracking(orgContacts as ContactWithRole[]);
            }
        }
    }

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }
}

